import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, X } from 'lucide-react';
import { AppUser, Listing } from '../../../types';
import { useSuperAdminActions } from '../../../hooks/useListingAdmin';
import { pendingQueue, REJECTION_REASON_MAX } from '../../../utils/superAdmin';
import { displayAddress, primaryPhone } from '../../../utils/listingDisplay';
import CategoryIcon from '../../directory/CategoryIcon';
import { TextArea } from '../listing/formFields';
import EmailComposer from './EmailComposer';
import { formatDate, userLabel } from './superFormat';

const BUTTON = 'flex-1 inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 rounded-[10px] text-sm font-medium transition-opacity disabled:opacity-40';

/** After Approve / Reject: the email to the owner, which Yoel can change, send or skip (docs/email-plan.md). */
interface FollowUp {
  listing: Listing;
  kind: 'approved' | 'rejected';
  reason: string;
}

/** Pending listings from the public: approve or reject with a reason (plan 10.5). */
export default function ApprovalsTab({ listings, users }: { listings: Listing[]; users: AppUser[] }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const queue = pendingQueue(listings);
  // Kept here: once approved or rejected, the listing leaves the queue (and its card disappears).
  const [followUp, setFollowUp] = useState<FollowUp | null>(null);

  return (
    <div className="space-y-4">
      {followUp && (
        <div className="bg-paper border-2 border-ink rounded-[16px] p-4 space-y-3">
          <p className="font-semibold text-ink">
            {t(`directory.super.email.followUp.${followUp.kind}`, {
              name: (isHebrew ? followUp.listing.nameHe || followUp.listing.nameEn : followUp.listing.nameEn || followUp.listing.nameHe) ?? '',
            })}
          </p>
          <EmailComposer
            key={`${followUp.listing.id}-${followUp.kind}`}
            listing={followUp.listing}
            users={users}
            kind={followUp.kind}
            reason={followUp.reason}
            onDone={() => setFollowUp(null)}
          />
        </div>
      )}
      {queue.length === 0 ? (
        !followUp && <p className="text-sm text-ash">{t('directory.super.approvals.none')}</p>
      ) : (
        <>
          <p className="text-sm text-ash">{t('directory.super.approvals.intro')}</p>
          {queue.map((listing) => (
            <ApprovalCard key={listing.id} listing={listing} users={users} onDecided={setFollowUp} />
          ))}
        </>
      )}
    </div>
  );
}

function ApprovalCard({
  listing,
  users,
  onDecided,
}: {
  listing: Listing;
  users: AppUser[];
  onDecided: (f: FollowUp) => void;
}) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const { approve, reject, saving } = useSuperAdminActions();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [failed, setFailed] = useState(false);

  const sub = 'subcategory' in listing ? listing.subcategory : undefined;
  const address = displayAddress(listing, isHebrew);
  const phone = primaryPhone(listing);
  const description = (isHebrew ? listing.descriptionHe || listing.descriptionEn : listing.descriptionEn || listing.descriptionHe) ?? '';
  const applicants = (listing.ownerUids ?? []).map((uid) => users.find((u) => u.uid === uid));

  const act = async (fn: () => Promise<unknown>, after?: () => void) => {
    setFailed(false);
    try {
      await fn();
      after?.();
    } catch {
      setFailed(true);
    }
  };

  return (
    <div className="bg-paper border border-ink/10 rounded-[16px] p-4 space-y-3">
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-10 h-10 rounded-[10px] bg-fog flex items-center justify-center text-ink/70">
          <CategoryIcon category={listing.category} className="w-5 h-5" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1 space-y-0.5">
          <h3 className="font-semibold text-ink break-words" dir="rtl">{listing.nameHe}</h3>
          {listing.nameEn && <p className="text-sm text-ink break-words" dir="ltr">{listing.nameEn}</p>}
          <p className="text-xs text-ash">
            {t(`directory.category.${listing.category}`)}
            {sub ? ` · ${t(`directory.sub.${sub}`)}` : ''}
            {' · '}
            {t('directory.super.approvals.sent', { date: formatDate(listing.createdAt, isHebrew) })}
          </p>
        </div>
      </div>

      <div className="space-y-1 text-sm text-ink">
        {address && <p>{address}</p>}
        {phone && <p dir="ltr" className="text-start">{phone}</p>}
        {description && <p className="text-ash line-clamp-3 whitespace-pre-line">{description}</p>}
      </div>

      <div className="rounded-[10px] bg-fog px-3 py-2 space-y-0.5">
        <p className="text-xs font-medium text-ash">{t('directory.super.approvals.sentBy')}</p>
        {applicants.length === 0 ? (
          <p className="text-sm text-ink">{t('directory.super.owners.none')}</p>
        ) : (
          applicants.map((u, i) => (
            <p key={u?.uid ?? i} className="text-sm text-ink break-all">
              {u ? <bdi>{userLabel(u)}</bdi> : t('directory.super.approvals.unknown')}
            </p>
          ))
        )}
      </div>

      <Link to={`/admin?item=${listing.id}`} className="inline-flex items-center gap-1 min-h-[44px] text-sm text-ink hover:underline">
        <Pencil className="w-4 h-4" aria-hidden />
        {t('directory.super.approvals.open')}
      </Link>

      {rejecting ? (
        <div className="space-y-2">
          <TextArea
            label={t('directory.super.approvals.reason')}
            hint={t('directory.super.approvals.reasonHint')}
            value={reason}
            onChange={setReason}
            maxLength={REJECTION_REASON_MAX}
          />
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => act(() => reject(listing.id, reason), () => onDecided({ listing, kind: 'rejected', reason }))}
              className={`${BUTTON} bg-danger-600 text-paper`}
            >
              {t('directory.super.approvals.confirmReject')}
            </button>
            <button type="button" disabled={saving} onClick={() => setRejecting(false)} className={`${BUTTON} border border-ink text-ink`}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <button type="button" disabled={saving} onClick={() => act(() => approve(listing.id), () => onDecided({ listing, kind: 'approved', reason: '' }))} className={`${BUTTON} bg-ink text-paper`}>
            <Check className="w-4 h-4" aria-hidden />
            {t('directory.super.approvals.approve')}
          </button>
          <button type="button" disabled={saving} onClick={() => setRejecting(true)} className={`${BUTTON} border border-ink text-ink`}>
            <X className="w-4 h-4" aria-hidden />
            {t('directory.super.approvals.reject')}
          </button>
        </div>
      )}
      {failed && <p role="alert" className="text-sm text-danger-600">{t('directory.admin.saveFailed')}</p>}
    </div>
  );
}
